import React, { FunctionComponent } from "react";
import { Label } from "src/ui";
import "./TableWidget.scss";

// Interface for customer-specific DSL format
interface CustomerDataFormat {
    userData: Array<{
        id: string | number;
        sum: string | number;
        items: Array<{
            key: string;
            value: string | number;
        }>;
    }>;
    headers: Array<{
        headerKey: string;
    }>;
}

// Type for generic 2D array format
type GenericTableFormat = Array<Array<string | number>>;

export interface ITableWidgetProps {
    items: CustomerDataFormat | GenericTableFormat | CustomerDataFormat[];
    variables?: { [key: string]: string };
}

const TableWidget: FunctionComponent<ITableWidgetProps> = (props) => {
    const { items, variables } = props;
    const leftHeaderLabel = variables?.leftHeaderLabel || "ID";
    const rightHeaderLabel = variables?.rightHeaderLabel || "Sum";

    // Type guard for customer-specific format
    const isCustomerFormat = (data: any): data is CustomerDataFormat => {
        return (
            data &&
            typeof data === 'object' &&
            !Array.isArray(data) &&
            Array.isArray(data.userData) &&
            Array.isArray(data.headers)
        );
    };

    // Type guard for generic table format
    const isGenericTableFormat = (data: any): data is GenericTableFormat => {
        return (
            Array.isArray(data) &&
            data.length > 1 &&
            Array.isArray(data[0])
        );
    };

    // Normalize items to a consistent format for rendering
    let normalizedItems: CustomerDataFormat | GenericTableFormat;
    if (Array.isArray(items) && items.length > 0 && isCustomerFormat(items[0])) {
        normalizedItems = items[0];
    } else if (isCustomerFormat(items) || isGenericTableFormat(items)) {
        normalizedItems = items as CustomerDataFormat | GenericTableFormat;
    } else {
        console.error("TableWidget: unsupported items format");
        return null;
    }

    // Utility function to render a header cell
    const renderHeaderCell = (content: string, className: string = "key-column", key?: string | number) => (
        <th key={key} className={`header-cell ${className}`}>
            <Label size="body-2" weight="medium" color="dark-grey">
                {content}
            </Label>
        </th>
    );

    // Utility function to render a data cell
    const renderDataCell = (content: string | number, className: string = "key-column", isBold: boolean = false, key?: string | number) => (
        <td key={key} className={`data-cell ${className}`}>
            <Label size="body-2" weight={isBold ? "medium" : undefined} color={isBold ? "dark-grey" : "mid-grey"}>
                {content}
            </Label>
        </td>
    );

    // -----------------------------------------
    // CASE 1 – Customer's specific DSL format
    // -----------------------------------------

    if (isCustomerFormat(normalizedItems)) {
        const { userData, headers } = normalizedItems;

        return (
            <div className="TableWidget">
                <div className="table-container">
                    <table
                        className="table"
                        style={{
                            ['--table-columns' as any]: 2 + (headers?.length || 0) // 1 for id, headers.length for keys, 1 for sum
                        }}
                    >
                        <thead>
                            <tr>
                                {renderHeaderCell(leftHeaderLabel, "id-column", "id-header")}
                                {headers.map((header, index) => 
                                    renderHeaderCell(header.headerKey, "key-column", `header-${index}`)
                                )}
                                {renderHeaderCell(rightHeaderLabel, "sum-column", "sum-header")}
                            </tr>
                        </thead>
                        <tbody>
                            {userData.map((user, userIndex) => {
                                const valuesMap = user.items.reduce((acc, item) => {
                                    acc[item.key] = item.value;
                                    return acc;
                                }, {} as Record<string, string | number>);

                                return (
                                    <tr key={user.id || userIndex} className="data-row">
                                        {renderDataCell(user.id, "id-column", true, "id")}
                                        {headers.map((header, headerIndex) => 
                                            renderDataCell(valuesMap[header.headerKey] || 0, "key-column", false, `value-${headerIndex}`)
                                        )}
                                        {renderDataCell(user.sum, "sum-column", true, "sum")}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // -----------------------------------------
    // CASE 2 – Generic table format
    // -----------------------------------------

    if (isGenericTableFormat(normalizedItems)) {
        const [headerRow, ...dataRows] = normalizedItems;

        return (
            <div className="TableWidget generic-table">
                <div className="table-container">
                    <table
                        className="table"
                        style={{
                            ['--table-columns' as any]: headerRow.length
                        }}
                    >
                        <thead>
                            <tr>
                                {headerRow.map((header, index) => 
                                    renderHeaderCell(String(header), index === 0 ? "generic-first-column" : "generic-value-column", `generic-header-${index}`)
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {dataRows.map((row, rowIndex) => (
                                <tr key={rowIndex} className="data-row">
                                    {row.map((cell, cellIndex) => 
                                        renderDataCell(cell, cellIndex === 0 ? "generic-first-column" : "generic-value-column", cellIndex === 0, `generic-cell-${cellIndex}`)
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // -----------------------------------------
    // Fallback – no valid data
    // -----------------------------------------
    
    return (
        <div className="TableWidget">
            <Label size="body-2" color="mid-grey">
                No data available
            </Label>
        </div>
    );
};

export default TableWidget;
